// Web renderer registry: the dispatch-by-step-type map, mirroring the desktop
// QtPresenter's dispatch. One renderer per step type, each reproducing its
// desktop counterpart's behavior, timing, and look (parity is acceptance).
//
// The interface is finalized here (Phase 4 step 4.1). Renderers are filled in
// 4.2 (the chat-only slice: landing/input gate, consent, chat, debrief) and
// Phase 5 (the rest). Adding a screen type is purely additive: implement a
// component with this signature and register it below.

import type { ComponentType } from "react";
import type { StepType, StepEnvelope, StepResult } from "../api/types";
import type { SessionHandle } from "../session/sessionDriver";
import ConsentRenderer from "./ConsentRenderer";
import DebriefRenderer from "./DebriefRenderer";
import ChatRenderer from "./ChatRenderer";
import TransitionRenderer from "./TransitionRenderer";
import WritingRenderer from "./WritingRenderer";
import SurveyRenderer from "./SurveyRenderer";
import InputGateRenderer from "./InputGateRenderer";
import DocumentRenderer from "./DocumentRenderer";
import InstructionRenderer from "./InstructionRenderer";
import VideoRenderer from "./VideoRenderer";
import TaskRenderer from "./TaskRenderer";
import BiometricsNoticeRenderer from "./BiometricsNoticeRenderer";

// How a renderer reports it is finished:
//   - "submit": a normal step produced a result; the controller submits it and
//     the SERVER advances to the next step.
//   - "advanced": the chat block already advanced the session server-side (its
//     streaming endpoint does this when the end condition is met); the
//     controller just re-syncs to the new current step, it does NOT submit.
export type RendererOutcome =
  | { kind: "submit"; result: StepResult }
  | { kind: "advanced" };

export interface WebRendererProps {
  step: StepEnvelope;
  session: SessionHandle;
  onDone: (outcome: RendererOutcome) => void;
}

export type WebRenderer = ComponentType<WebRendererProps>;

export const RENDERERS: Partial<Record<StepType, WebRenderer>> = {
  consent: ConsentRenderer, // Phase 4.2
  debrief: DebriefRenderer, // Phase 4.2
  block: ChatRenderer, // Phase 4.2b (uses the streaming chat endpoint)
  transition: TransitionRenderer, // Phase 5
  writing: WritingRenderer, // Phase 5
  survey: SurveyRenderer, // Phase 5
  input_gate: InputGateRenderer, // Phase 5
  document: DocumentRenderer, // Phase 5
  instruction: InstructionRenderer, // typed instruction screen (title + Markdown + button)
  video: VideoRenderer, // QoL Batch 3 item 8 (native video block)
  task: TaskRenderer, // Phase 5
  biometrics_notice: BiometricsNoticeRenderer, // provenance: participant notice
};
